import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api } from "../api";

const TrainingSessionContext = createContext(null);

export const useTrainingSession = () => {
  const context = useContext(TrainingSessionContext);

  if (!context) {
    throw new Error(
      "useTrainingSession must be used inside TrainingSessionProvider"
    );
  }

  return context;
};

/*
 * ---------------------------------------------------------------------------
 * 1471 CADET TRAINING SESSION
 * ---------------------------------------------------------------------------
 *
 * The tablet authenticates the DEVICE.
 *
 * The service number identifies the anonymous training record.
 *
 * Example:
 *
 *   3014710026001
 *
 *   30    = fixed prefix
 *   1471  = squadron
 *   0026  = 2026
 *   001   = first cadet enrolled that year
 *
 * The website does not need the cadet's name.
 */

export const TRAINING_ACTIVITIES = {
  NEW_STARTER: "newstarter",
  FIRST_CLASS: "firstclass",
  LEADING: "leading",
};

export const TRAINING_ACTIVITY_LABELS = {
  newstarter: "New Starter",
  firstclass: "First Class",
  leading: "Leading Cadet",
};

export const TRAINING_ACTIVITY_PATHS = {
  newstarter: "/training/newstarter",
  firstclass: "/training/firstclass",
  leading: "/training/leading",
};

/*
 * Valid 1471 service number:
 *
 * 30147100YYNNN
 *
 * e.g.
 *
 * 3014710026001
 */
const SERVICE_NUMBER_REGEX =
  /^30147100\d{2}\d{3}$/;

/*
 * ---------------------------------------------------------------------------
 * Service-number helpers
 * ---------------------------------------------------------------------------
 */

export function cleanServiceNumber(value) {
  return String(value || "")
    .replace(/[\s-]/g, "")
    .replace(/\D/g, "")
    .slice(0, 13);
}

export function isValidServiceNumber(value) {
  const number = cleanServiceNumber(value);

  if (!SERVICE_NUMBER_REGEX.test(number)) {
    return false;
  }

  /*
   * Final three digits are the annual sequence.
   * 000 is not permitted.
   */
  const sequence = Number(number.slice(-3));

  return sequence >= 1;
}

export function formatServiceNumber(value) {
  const number = cleanServiceNumber(value);

  if (number.length !== 13) {
    return number;
  }

  /*
   * Display:
   *
   * 30 1471 0026 001
   *
   * The server still receives:
   *
   * 3014710026001
   */
  return [
    number.slice(0, 2),
    number.slice(2, 6),
    number.slice(6, 10),
    number.slice(10, 13),
  ].join(" ");
}

/*
 * ---------------------------------------------------------------------------
 * Provider
 * ---------------------------------------------------------------------------
 */

export function TrainingSessionProvider({
  children,
}) {
  /*
   * Server session
   */
  const [active, setActive] = useState(false);

  const [serviceNumber, setServiceNumber] =
    useState("");

  const [activity, setActivity] =
    useState("");

  const [record, setRecord] =
    useState(null);

  const [activityProgress, setActivityProgress] =
    useState(null);

  /*
   * Page/course state
   */
  const [currentStep, setCurrentStepState] =
    useState("");

  const [sessionData, setSessionDataState] =
    useState({});

  /*
   * UI state
   */
  const [loading, setLoading] =
    useState(true);

  const [starting, setStarting] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [ending, setEnding] =
    useState(false);

  const [error, setError] =
    useState("");

  /*
   * When a session ends this remains available long enough
   * for the cadet to see their bookmark/service number.
   */
  const [bookmarkCode, setBookmarkCode] =
    useState("");

  /*
   * Refs allow a pending autosave to always use the latest
   * values rather than an old React render.
   */
  const currentStepRef = useRef("");
  const sessionDataRef = useRef({});
  const saveTimerRef = useRef(null);

  /*
   * -----------------------------------------------------------------------
   * Internal helpers
   * -----------------------------------------------------------------------
   */

  const setCurrentStep = useCallback(
    (value) => {
      const next = String(value || "");

      currentStepRef.current = next;
      setCurrentStepState(next);
    },
    []
  );

  const setSessionData = useCallback(
    (value) => {
      const next =
        typeof value === "function"
          ? value(sessionDataRef.current)
          : value;

      const safe =
        next &&
        typeof next === "object" &&
        !Array.isArray(next)
          ? next
          : {};

      sessionDataRef.current = safe;
      setSessionDataState(safe);
    },
    []
  );

  /*
   * Merge a small update into the activity state.
   *
   * Example:
   *
   * patchSessionData({
   *   uniformComplete: true
   * })
   */
  const patchSessionData = useCallback(
    (patch) => {
      setSessionData((previous) => ({
        ...previous,
        ...patch,
      }));
    },
    [setSessionData]
  );

  const cancelPendingSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, []);

  const resetLocalSession = useCallback(() => {
    cancelPendingSave();

    setActive(false);
    setServiceNumber("");
    setActivity("");
    setRecord(null);
    setActivityProgress(null);

    currentStepRef.current = "";
    sessionDataRef.current = {};

    setCurrentStepState("");
    setSessionDataState({});
  }, [cancelPendingSave]);

  /*
   * -----------------------------------------------------------------------
   * Restore an active session from server response
   * -----------------------------------------------------------------------
   */

  const applyActiveSession = useCallback(
    (response) => {
      if (!response) {
        return;
      }

      const number =
        response.service_number ||
        response.session?.service_number ||
        response.record?.service_number ||
        "";

      const activityKey =
        response.activity ||
        response.session?.activity ||
        response.record?.last_activity ||
        "";

      const progress =
        response.activity_progress ||
        response.record?.activities?.[
          activityKey
        ] ||
        {};

      const step =
        progress?.current_step ||
        response.session?.current_step ||
        "";

      const data =
        progress?.state &&
        typeof progress.state === "object"
          ? progress.state
          : {};

      setActive(true);
      setServiceNumber(number);
      setActivity(activityKey);

      setRecord(
        response.record || null
      );

      setActivityProgress(progress);

      currentStepRef.current = step;
      sessionDataRef.current = data;

      setCurrentStepState(step);
      setSessionDataState(data);

      setError("");
      setBookmarkCode("");
    },
    []
  );

  /*
   * -----------------------------------------------------------------------
   * Check whether this tablet already has a session open
   * -----------------------------------------------------------------------
   *
   * This protects against:
   *
   * - page refresh
   * - browser restart
   * - accidental navigation
   *
   * The learner should return to where they were rather than
   * silently losing the current tablet session.
   */

  const restoreActiveSession =
    useCallback(async () => {
      try {
        const { data } = await api.get(
          "/cadet-training/active"
        );

        if (!data?.active) {
          resetLocalSession();
          return null;
        }

        applyActiveSession(data);

        return data;
      } catch (err) {
        setError(
          err?.response?.data?.detail ||
            "Unable to check the current training session."
        );

        return null;
      }
    }, [
      applyActiveSession,
      resetLocalSession,
    ]);

  /*
   * Initial load.
   */
  useEffect(() => {
    let mounted = true;

    const initialise = async () => {
      try {
        await restoreActiveSession();
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initialise();

    return () => {
      mounted = false;

      if (saveTimerRef.current) {
        clearTimeout(
          saveTimerRef.current
        );
      }
    };
  }, [restoreActiveSession]);

  /*
   * -----------------------------------------------------------------------
   * Start / resume training
   * -----------------------------------------------------------------------
   */

  const startSession = useCallback(
    async (
      rawServiceNumber,
      selectedActivity
    ) => {
      const number =
        cleanServiceNumber(
          rawServiceNumber
        );

      if (!isValidServiceNumber(number)) {
        throw new Error(
          "Enter a valid 1471 service number, for example 3014710026001."
        );
      }

      if (
        !Object.values(
          TRAINING_ACTIVITIES
        ).includes(selectedActivity)
      ) {
        throw new Error(
          "Select a training activity."
        );
      }

      setStarting(true);
      setError("");
      setBookmarkCode("");

      try {
        const { data } = await api.post(
          "/cadet-training/session/start",
          {
            service_number: number,
            activity: selectedActivity,
          }
        );

        applyActiveSession(data);

        return data;
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to start training.";

        setError(message);

        throw new Error(message);
      } finally {
        setStarting(false);
      }
    },
    [applyActiveSession]
  );

  /*
   * -----------------------------------------------------------------------
   * Change activity while keeping the same learner
   * -----------------------------------------------------------------------
   *
   * For example:
   *
   * New Starter → First Class
   *
   * This uses the same service number.
   */

  const changeActivity = useCallback(
    async (nextActivity) => {
      if (!serviceNumber) {
        throw new Error(
          "No training record is currently open."
        );
      }

      /*
       * Make sure the old activity is saved first.
       */
      cancelPendingSave();

      if (activity) {
        try {
          await api.put(
            `/cadet-training/session/${serviceNumber}/state`,
            {
              activity,
              current_step:
                currentStepRef.current,
              data:
                sessionDataRef.current,
            }
          );
        } catch (err) {
          const message =
            err?.response?.data?.detail ||
            "Unable to save the current activity.";

          setError(message);

          throw new Error(message);
        }
      }

      return await startSession(
        serviceNumber,
        nextActivity
      );
    },
    [
      activity,
      cancelPendingSave,
      serviceNumber,
      startSession,
    ]
  );

  /*
   * -----------------------------------------------------------------------
   * Save activity state
   * -----------------------------------------------------------------------
   */

  const saveState = useCallback(
    async (
      stepOverride,
      dataOverride
    ) => {
      if (
        !active ||
        !serviceNumber ||
        !activity
      ) {
        return null;
      }

      cancelPendingSave();

      const step =
        stepOverride !== undefined
          ? String(
              stepOverride || ""
            )
          : currentStepRef.current;

      const state =
        dataOverride !== undefined
          ? dataOverride
          : sessionDataRef.current;

      setSaving(true);
      setError("");

      try {
        const { data } = await api.put(
          `/cadet-training/session/${serviceNumber}/state`,
          {
            activity,
            current_step: step,
            data:
              state &&
              typeof state === "object"
                ? state
                : {},
          }
        );

        return data;
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          "Unable to save training progress.";

        setError(message);

        throw new Error(message);
      } finally {
        setSaving(false);
      }
    },
    [
      active,
      activity,
      cancelPendingSave,
      serviceNumber,
    ]
  );

  /*
   * -----------------------------------------------------------------------
   * Debounced autosave
   * -----------------------------------------------------------------------
   *
   * Course pages can call:
   *
   * queueSave()
   *
   * after changing an answer or moving to another section.
   *
   * Multiple rapid changes are combined into one server request.
   */

  const queueSave = useCallback(
    (
      delay = 800
    ) => {
      if (
        !active ||
        !serviceNumber ||
        !activity
      ) {
        return;
      }

      cancelPendingSave();

      saveTimerRef.current =
        setTimeout(() => {
          saveTimerRef.current = null;

          saveState().catch(() => {
            /*
             * saveState already exposes the
             * error through context.
             */
          });
        }, delay);
    },
    [
      active,
      activity,
      cancelPendingSave,
      saveState,
      serviceNumber,
    ]
  );

  /*
   * Convenience function for changing both the page and its
   * stored data, followed by an autosave.
   */
  const updateProgress = useCallback(
    (
      step,
      newData,
      autosave = true
    ) => {
      if (step !== undefined) {
        setCurrentStep(step);
      }

      if (newData !== undefined) {
        setSessionData(newData);
      }

      if (autosave) {
        /*
         * Let React/state refs update first.
         */
        setTimeout(() => {
          queueSave();
        }, 0);
      }
    },
    [
      queueSave,
      setCurrentStep,
      setSessionData,
    ]
  );

  /*
   * -----------------------------------------------------------------------
   * Module completion
   * -----------------------------------------------------------------------
   */

  const updateModule = useCallback(
    async (
      moduleId,
      status = "in_progress",
      score = null
    ) => {
      if (
        !active ||
        !serviceNumber ||
        !activity
      ) {
        throw new Error(
          "No training session is open."
        );
      }

      try {
        const { data } = await api.put(
          `/cadet-training/session/${serviceNumber}/module`,
          {
            activity,
            module_id: moduleId,
            status,
            score,
          }
        );

        /*
         * Refresh the session summary so progress bars
         * immediately reflect the change.
         */
        await refreshSession();

        return data;
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          "Unable to update this training module.";

        setError(message);

        throw new Error(message);
      }
    },
    [
      active,
      activity,
      serviceNumber,
    ]
  );

  /*
   * -----------------------------------------------------------------------
   * Refresh record
   * -----------------------------------------------------------------------
   */

  const refreshSession = useCallback(
    async () => {
      if (
        !serviceNumber ||
        !active
      ) {
        return null;
      }

      try {
        const { data } = await api.get(
          `/cadet-training/session/${serviceNumber}`
        );

        const activityKey =
          data?.session?.activity ||
          activity;

        setRecord(
          data?.record || null
        );

        setActivityProgress(
          data?.activity_progress ||
            data?.record?.activities?.[
              activityKey
            ] ||
            null
        );

        return data;
      } catch (err) {
        setError(
          err?.response?.data?.detail ||
            "Unable to refresh training progress."
        );

        return null;
      }
    },
    [
      active,
      activity,
      serviceNumber,
    ]
  );

  /*
   * -----------------------------------------------------------------------
   * Complete an activity
   * -----------------------------------------------------------------------
   */

  const setActivityComplete =
    useCallback(
      async (
        completed = true
      ) => {
        if (
          !serviceNumber ||
          !activity
        ) {
          throw new Error(
            "No training activity is open."
          );
        }

        /*
         * Save the current page first.
         */
        await saveState();

        try {
          const { data } = await api.put(
            `/cadet-training/session/${serviceNumber}/activity/${activity}/complete`,
            {
              completed,
            }
          );

          await refreshSession();

          return data;
        } catch (err) {
          const message =
            err?.response?.data?.detail ||
            "Unable to update activity completion.";

          setError(message);

          throw new Error(message);
        }
      },
      [
        activity,
        refreshSession,
        saveState,
        serviceNumber,
      ]
    );

  /*
   * -----------------------------------------------------------------------
   * END SESSION & SAVE
   * -----------------------------------------------------------------------
   *
   * This is the important shared-tablet operation.
   *
   * It:
   *
   *   1. saves the final page state
   *   2. releases the tablet
   *   3. returns the service number as the bookmark code
   *   4. clears the active learner from the tablet UI
   */

  const endSession = useCallback(
    async () => {
      if (
        !active ||
        !serviceNumber ||
        !activity
      ) {
        return null;
      }

      cancelPendingSave();

      setEnding(true);
      setError("");

      try {
        const number =
          serviceNumber;

        const { data } =
          await api.post(
            "/cadet-training/session/end",
            {
              service_number: number,
              activity,
              current_step:
                currentStepRef.current,
              data:
                sessionDataRef.current,
            }
          );

        const bookmark =
          data?.bookmark_code ||
          number;

        /*
         * Clear the learner from this tablet.
         */
        resetLocalSession();

        /*
         * But retain this for the confirmation screen.
         */
        setBookmarkCode(bookmark);

        return {
          ...data,
          bookmark_code: bookmark,
        };
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          "Unable to end the session. Your training record has not been released from this tablet.";

        setError(message);

        throw new Error(message);
      } finally {
        setEnding(false);
      }
    },
    [
      active,
      activity,
      cancelPendingSave,
      resetLocalSession,
      serviceNumber,
    ]
  );

  /*
   * After the cadet has read their bookmark code, the
   * confirmation screen can call this.
   */
  const dismissBookmark =
    useCallback(() => {
      setBookmarkCode("");
    }, []);

  /*
   * -----------------------------------------------------------------------
   * Derived activity summaries
   * -----------------------------------------------------------------------
   */

  const activities = useMemo(() => {
    const serverActivities =
      record?.activities || {};

    return {
      newstarter:
        serverActivities.newstarter || {
          completed: false,
          module_count: 0,
          modules_complete: 0,
          current_step: "",
          state: {},
          modules: [],
        },

      firstclass:
        serverActivities.firstclass || {
          completed: false,
          module_count: 0,
          modules_complete: 0,
          current_step: "",
          state: {},
          modules: [],
        },

      leading:
        serverActivities.leading || {
          completed: false,
          module_count: 0,
          modules_complete: 0,
          current_step: "",
          state: {},
          modules: [],
        },
    };
  }, [record]);

  /*
   * -----------------------------------------------------------------------
   * Context
   * -----------------------------------------------------------------------
   */

  const value = {
    /*
     * Session
     */
    active,
    serviceNumber,
    activity,

    /*
     * Server record
     */
    record,
    activityProgress,
    activities,

    /*
     * Current page state
     */
    currentStep,
    sessionData,

    /*
     * Status
     */
    loading,
    starting,
    saving,
    ending,
    error,
    setError,

    /*
     * Bookmark shown after ending
     */
    bookmarkCode,
    dismissBookmark,

    /*
     * Session actions
     */
    startSession,
    changeActivity,
    restoreActiveSession,
    refreshSession,
    endSession,

    /*
     * Progress actions
     */
    setCurrentStep,
    setSessionData,
    patchSessionData,
    updateProgress,
    saveState,
    queueSave,
    updateModule,
    setActivityComplete,

    /*
     * Helpers
     */
    cleanServiceNumber,
    isValidServiceNumber,
    formatServiceNumber,

    TRAINING_ACTIVITIES,
    TRAINING_ACTIVITY_LABELS,
    TRAINING_ACTIVITY_PATHS,
  };

  return (
    <TrainingSessionContext.Provider
      value={value}
    >
      {children}
    </TrainingSessionContext.Provider>
  );
}

export default TrainingSessionContext;